import type { ComputedRef, Ref } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'

import { useListsStore } from '@shared/model'

import { useKidStore } from './store'
import { useKidDetailsApi } from '../api/details.hook'
import { useKidDisciplineStoreApi } from '../api/discipline-store.hook'

interface ModalKidAddDisciplineReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: ComputedRef<boolean>
  refTitle: ComputedRef<string>
  refCode: Ref<string | null>
  disciplineOptions: ComputedRef<NativeSelectOption[]>
  isSubmitDisabled: ComputedRef<boolean>
  setCode: (option: NativeSelectOption) => void
  closeModal: () => void
  submit: () => void
}

export const useModalKidAddDiscipline = (): ModalKidAddDisciplineReturn => {
  const kidStore = useKidStore()
  const listsStore = useListsStore()

  const refCode = ref<string | null>(null)

  const refIsOpen = computed<boolean>(() => kidStore.isModalAddDisciplineOpen)
  const refIsLoading = computed<boolean>(() => kidStore.isProcessing)
  const refTitle = computed<string>(() => `Add discipline for ${kidStore.getKid?.name ?? ''}`)

  const disciplineOptions = computed<NativeSelectOption[]>(() => {
    const takenCodes = kidStore.getDisciplines.map(discipline => discipline.code)

    return Object.entries(listsStore.getDiscipline?.code ?? {}).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refCode.value,
      disabled: takenCodes.includes(value),
    }))
  })

  const isSubmitDisabled = computed<boolean>(() => refIsLoading.value || !refCode.value)

  const setCode = (option: NativeSelectOption): void => {
    refCode.value = option.value === null ? null : String(option.value)
  }

  const closeModal = (): void => {
    refCode.value = null
    kidStore.setModalAddDiscipline(false)
  }

  /** The modal stays open with its loader until the kid is re-read; any failure toasts and keeps it open. */
  const store = async (): Promise<void> => {
    const kid = kidStore.getKid
    if (!kid || !refCode.value) return

    kidStore.trackProcessing(true)

    try {
      const discipline = await useKidDisciplineStoreApi({
        kid_id: kid.id,
        code: refCode.value,
      })
      if (!discipline) return

      const refreshedKid = await useKidDetailsApi({
        kidId: kid.id,
        onLoadingChange: kidStore.trackProcessing,
      })
      if (!refreshedKid) return

      closeModal()
    } finally {
      kidStore.trackProcessing(false)
    }
  }

  const submit = (): void => {
    void store()
  }

  return {
    refIsOpen,
    refIsLoading,
    refTitle,
    refCode,
    disciplineOptions,
    isSubmitDisabled,
    setCode,
    closeModal,
    submit,
  }
}
